import * as React from 'react';
import { useCallback } from 'react';
import { Collapse } from '@mui/material';
import cn from 'classnames';
import { CheckoutSteps } from '../types';
import { ThemeProvider, useTheme } from '../../theme';
import { useCartPrices } from '../../Cart/hooks';
import { useMobileStepbar } from '../hooks';
import Cart from '../../Cart/Cart';
import StepbarHeader from './StepbarHeader/StepbarHeader';
import StepbarServiceSummary from './StepbarServiceSummary/StepbarServiceSummary';
import Dropdown from '../../Dropdown';
import { STEP_ICON_NAME_BY_TYPE } from '../../BaseComponents/Stepbar/Icons';
import { useConfig } from '../../context';
import { CdnIcon } from '@shared/ui';
const MobileStepbar = ({ alwaysOpen, order, currentStep, stepbarConfig, hideFlightPrice, onServiceDelete }) => {
    const { MobileStepbarStyles: css } = useTheme('MobileStepbar');
    const { stepsAvailable } = useConfig().global;
    const cartPrices = useCartPrices();
    const { isOpen, toggle, close, cartServiceProps, cartServices, availableSteps, additionalSteps, isBookedOrConfirmed, isCheckoutOrder, isCancelled, isPayment } = useMobileStepbar(order, stepbarConfig, alwaysOpen);
    const onClickStep = useCallback((onClick) => () => {
        close();
        onClick();
    }, [isOpen]);
    const renderService = useCallback((type, group, content) => {
        var _a, _b;
        let actualType = type;
        let serviceSummary = group;
        const isExtras = actualType === CheckoutSteps.Extras;
        // hide extras for checkout order
        if (isExtras && isCheckoutOrder && !content) {
            return null;
        }
        const isPassengers = actualType === CheckoutSteps.Passengers;
        const isOrderBookedOrConfirmedStep = isPassengers && isCheckoutOrder && isBookedOrConfirmed;
        const isOrderCancelledStep = isPassengers && isCheckoutOrder && isCancelled;
        const isBookedOrderHasDisabledMealStep = isBookedOrConfirmed && stepsAvailable[CheckoutSteps.Meal] === false;
        // after booking or cancelled, the Passenger step essentially becomes the Order step, so we replace the type and information
        if ((isOrderBookedOrConfirmedStep || isOrderCancelledStep) &&
            !additionalSteps.includes(CheckoutSteps.Order)) {
            actualType = CheckoutSteps.Order;
            serviceSummary = Object.assign(Object.assign({}, serviceSummary), { header: (_b = (_a = stepbarConfig[CheckoutSteps.Order]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : serviceSummary.header, icon: React.createElement(CdnIcon, { icon: STEP_ICON_NAME_BY_TYPE[CheckoutSteps.Order] }) });
        }
        const stepIndex = availableSteps.findIndex(step => step.type === actualType);
        const stepByType = availableSteps[stepIndex];
        let serviceClick;
        if (stepByType === null || stepByType === void 0 ? void 0 : stepByType.onClick) {
            serviceClick = onClickStep(stepByType.onClick);
        }
        if ((isOrderBookedOrConfirmedStep ||
            isOrderCancelledStep ||
            isPayment ||
            (type === CheckoutSteps.Meal && isBookedOrderHasDisabledMealStep)) &&
            serviceClick) {
            serviceClick = null;
        }
        const isPassed = stepIndex < currentStep;
        const isActive = stepIndex === currentStep;
        const isSelected = isPassengers ? (isPassed || isActive) && !!content : !!content;
        return (React.createElement(StepbarServiceSummary, { type: actualType, group: serviceSummary, isPassed: isPassed, isActive: isActive, isSelected: isSelected, onClick: serviceClick }, content));
    }, [currentStep, availableSteps, additionalSteps, isBookedOrConfirmed, isCheckoutOrder, isPayment]);
    const renderServices = useCallback(() => {
        if (additionalSteps.length) {
            return additionalSteps.map(additionalStep => {
                const stepIndex = availableSteps.findIndex(step => step.type === additionalStep);
                const stepByType = availableSteps[stepIndex];
                return (React.createElement(StepbarServiceSummary, { key: additionalStep, type: additionalStep, group: {
                        header: stepByType === null || stepByType === void 0 ? void 0 : stepByType.label,
                        icon: React.createElement(CdnIcon, { icon: STEP_ICON_NAME_BY_TYPE[additionalStep] })
                    }, isPassed: stepIndex < currentStep, isActive: stepIndex === currentStep, isSelected: false }));
            });
        }
        return null;
    }, [currentStep, availableSteps, additionalSteps]);
    return (React.createElement(Dropdown, { isOpen: true, onClose: close },
        React.createElement("div", { className: css.stepbar },
            React.createElement(StepbarHeader, { currentStep: currentStep, steps: availableSteps, totalPrice: cartPrices.total, isOpen: isOpen, onToggle: !alwaysOpen ? toggle : null }),
            React.createElement(Collapse, { in: isOpen },
                React.createElement(ThemeProvider, { value: {
                        Cart: {
                            Cart: { wrapper: css.stepbar__cartWrapper }
                        },
                        ServiceSummary: {
                            ServiceSummaryOption: {
                                option__price_icon: cn({
                                    [css.stepbar_hidden]: isPayment
                                })
                            }
                        }
                    } },
                    React.createElement(Cart, Object.assign({}, cartServiceProps, { prices: cartPrices, cartServices: cartServices, renderService: renderService, renderServices: renderServices, onServiceDelete: onServiceDelete, hideFlightPrice: hideFlightPrice, showPassengers: true, hideToolbar: true })))))));
};
export default MobileStepbar;
